import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Logo, Footer } from "@/components/Layout";

const Shell = ({ children, testId }) => (
  <div className="min-h-screen flex flex-col">
    <header className="bg-[#13201A] h-16 flex items-center px-4 sm:px-10"><Link to="/"><Logo /></Link></header>
    <main className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="card p-6 sm:p-10 max-w-[520px] w-full fade-up" data-testid={testId}>{children}</div>
    </main>
    <Footer />
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState("pending");
  const [creationUrl, setCreationUrl] = useState("");

  useEffect(() => {
    const id = params.get("session_id");
    if (!id) return setState("error");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${id}`);
        if (data.payment_status === "paid") { setCreationUrl(data.creation_url || ""); return setState("paid"); }
        if (["expired", "failed"].includes(data.payment_status)) return setState("error");
      } catch { return setState("error"); }
      if (++tries < 15) timer = setTimeout(poll, 2000); else setState("slow");
    };
    poll();
    return () => clearTimeout(timer);
  }, [params]);

  if (state === "paid") return (
    <Shell testId="payment-success">
      <CheckCircle2 size={36} className="text-[#1E6B45]" />
      <h1 className="text-3xl font-bold tracking-tight mt-4">Bienvenue parmi les clubs fondateurs</h1>
      <p className="text-[15px] text-[#5B665F] mt-3 leading-relaxed">Votre paiement est confirmé et votre place est réservée. Hugo vous contacte très vite pour la mise en place. Un reçu vous a été envoyé par email.</p>
      {creationUrl
        ? <Link to={creationUrl} className="btn-primary mt-6" data-testid="payment-success-create-club">Créer mon club</Link>
        : <Link to="/" className="btn-primary mt-6" data-testid="payment-success-home">Retour à l'accueil</Link>}
    </Shell>
  );
  if (state === "error") return (
    <Shell testId="payment-error">
      <XCircle size={36} className="text-[#8A4B00]" />
      <h1 className="text-3xl font-bold tracking-tight mt-4">Paiement non confirmé</h1>
      <p className="text-[15px] text-[#5B665F] mt-3">Le paiement n'a pas abouti ou la session a expiré. Aucun montant n'a été débité.</p>
      <Link to="/#offre" className="btn-primary mt-6" data-testid="payment-error-retry">Réessayer</Link>
    </Shell>
  );
  return (
    <Shell testId="payment-pending">
      <Loader2 size={32} className="text-[#1E6B45] animate-spin" />
      <h1 className="text-2xl font-bold tracking-tight mt-4">Confirmation du paiement…</h1>
      <p className="text-[15px] text-[#5B665F] mt-3">{state === "slow" ? "La confirmation prend plus de temps que prévu. Rechargez la page dans un instant." : "Merci de patienter quelques secondes."}</p>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testId="payment-cancel">
      <h1 className="text-3xl font-bold tracking-tight">Paiement annulé</h1>
      <p className="text-[15px] text-[#5B665F] mt-3">Aucun montant n'a été débité. Votre place n'est pas réservée tant que le paiement n'est pas finalisé.</p>
      <div className="flex flex-wrap gap-3 mt-6">
        <Link to="/#offre" className="btn-primary" data-testid="payment-cancel-retry">Revenir à l'offre</Link>
        <Link to="/#rappel" className="btn-outline" data-testid="payment-cancel-callback">Être rappelé</Link>
      </div>
    </Shell>
  );
}
